import { useNavigate } from "@tanstack/react-router";
import { Button } from "@scoach/ui";
import { Close, Spark } from "@scoach/ui/icons";
import { useEffect } from "react";

export interface OnboardingVideoModalProps {
  open: boolean;
  onClose: () => void;
  primaryLabel?: string;
}

/**
 * First-login welcome & Company Hub setup prompt.
 * Replaces the legacy API-key setup videos with an actionable guide
 * pointing the user to configure their company workspace, team roles,
 * and live grounding procedures (Monday.com, SharePoint, Drive).
 */
export function OnboardingVideoModal({
  open,
  onClose,
}: OnboardingVideoModalProps) {
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function handleGoToCompany() {
    onClose();
    nav({ to: "/company" });
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div
        className="modal onboarding-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 640,
          borderRadius: 20,
          background: "var(--surface)",
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.12)",
          border: "1px solid var(--border-soft)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          className="modal-head"
          style={{
            padding: "24px 28px 20px",
            borderBottom: "1px solid var(--border-soft)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            background: "linear-gradient(180deg, rgba(99, 102, 241, 0.06) 0%, transparent 100%)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 14,
                background: "linear-gradient(135deg, var(--primary) 0%, #4F46E5 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 20,
                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
              }}
            >
              <Spark size={22} />
            </div>
            <div>
              <h3 style={{ fontSize: 19, fontWeight: 700, margin: 0, color: "var(--text-1)" }}>
                Welcome to SellSynq
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-2)", margin: "4px 0 0" }}>
                Set up your company workspace to unlock real-time meeting coaching &amp; battlecards.
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <Close size={18} />
          </button>
        </div>

        {/* Body: 3 Pillars */}
        <div className="modal-body" style={{ padding: "22px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Recommended First Steps in Company Hub:
          </div>

          {/* Card 1: Company Profile & Competitors */}
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              border: "1px solid var(--border-soft)",
              background: "var(--surface-hover)",
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                fontSize: 20,
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(99, 102, 241, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              🏢
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 2 }}>
                1. Company Profile &amp; Competitor Battlecards
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
                Define your products, value proposition, and competitors. When buyers raise objections during live calls, SellSynq whispers instant win-rate counterpoints.
              </div>
            </div>
          </div>

          {/* Card 2: Team Roles & Tailored Coaching */}
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              border: "1px solid var(--border-soft)",
              background: "var(--surface-hover)",
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                fontSize: 20,
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(16, 185, 129, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              👥
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 2 }}>
                2. Team Coaching Roles (Sales / AE, SDR, Solutions, Support)
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
                Select your specific coaching role and team roles. SellSynq dynamically customizes live suggestions and conversation analytics to your exact job function.
              </div>
            </div>
          </div>

          {/* Card 3: Procedures & Connectors */}
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              border: "1px solid var(--border-soft)",
              background: "var(--surface-hover)",
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                fontSize: 20,
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(245, 158, 11, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              📋
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 2 }}>
                3. Company Procedures &amp; Live Document Grounding
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
                Connect <strong>Monday.com</strong>, <strong>SharePoint / OneDrive</strong>, or <strong>Google Drive</strong>. SellSynq strictly enforces your company policies, pricing limits, and SLAs during customer interactions.
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className="modal-foot"
          style={{
            padding: "16px 28px",
            borderTop: "1px solid var(--border-soft)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "var(--surface-2)",
          }}
        >
          <button
            type="button"
            className="ghost-btn"
            onClick={onClose}
            style={{ fontSize: 13, color: "var(--text-3)" }}
          >
            I'll configure it later
          </button>
          <Button
            type="button"
            variant="primary"
            onClick={handleGoToCompany}
            style={{
              padding: "8px 20px",
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>Open Company Hub</span>
            <span>→</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
